// theme-mode.ts
// Shared light/dark mode state management. Promoted out of agate's
// app/themeMode.ts + app/theme.tsx so every BlueRock console app gets
// `useThemeMode()`/`setThemeMode()` and the `.dark` class sync for free
// instead of reimplementing it — corenet's own colors.css already ships a
// full `.dark` palette (see its module comment: "Apps toggle the .dark
// class on document.documentElement via their own ThemeProvider").
//
// Deliberately NOT a `ConfigProvider` wrapper: each app composes its own
// provider tree (popup containers, App/message bridges, chart theme
// context, etc.), so this only owns the two things every consumer needs
// verbatim — the mode store and the DOM class sync — not the JSX around it.

import { useEffect, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeModeStore {
  /** Subscribes the calling component to mode changes; re-renders on toggle. */
  useThemeMode(): ThemeMode;
  setThemeMode(mode: ThemeMode): void;
  toggleThemeMode(): void;
}

function readSystemMode(defaultMode: ThemeMode): ThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return defaultMode;
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return defaultMode;
  }
}

/**
 * Creates an independent light/dark mode store, persisted to localStorage
 * under `storageKey`. Call this once at module scope in your app (not
 * inside a component) — state lives in the closure, not in React, so it
 * survives remounts and stays in sync across every component that calls
 * the returned `useThemeMode()`.
 *
 * Scope `storageKey` to your app (e.g. `'agate:theme-mode'`,
 * `'bluerock:theme-mode'`) so multiple BlueRock console apps sharing an
 * origin don't collide or fight over the same key.
 */
export function createThemeModeStore(storageKey: string, defaultMode: ThemeMode = 'dark'): ThemeModeStore {
  function readStoredMode(): ThemeMode {
    if (typeof window === 'undefined') return defaultMode;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === 'light' || raw === 'dark') return raw;
    } catch {
      // localStorage unavailable (private mode, SSR) — fall through
    }
    return readSystemMode(defaultMode);
  }

  let currentMode: ThemeMode = readStoredMode();
  const listeners = new Set<() => void>();

  function subscribe(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }
  function getSnapshot(): ThemeMode {
    return currentMode;
  }
  function setThemeMode(mode: ThemeMode): void {
    if (mode === currentMode) return;
    currentMode = mode;
    try {
      window.localStorage.setItem(storageKey, mode);
    } catch {
      // best-effort; ignore write failures
    }
    listeners.forEach((cb) => cb());
  }
  function toggleThemeMode(): void {
    setThemeMode(currentMode === 'dark' ? 'light' : 'dark');
  }
  function useThemeMode(): ThemeMode {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  }

  return { useThemeMode, setThemeMode, toggleThemeMode };
}

/**
 * Syncs `mode` onto `document.documentElement`'s `.dark` class — what
 * corenet's colors.css keys its dark palette from — plus a `data-theme`
 * attribute for CSS that prefers an attribute selector. Call once near your
 * app root (e.g. inside the component that also renders `ConfigProvider`).
 */
export function useSyncDarkClass(mode: ThemeMode): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', mode === 'dark');
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);
}
